'use client';

import { useState, useEffect } from 'react';

interface Section {
  id: string;
  label: string;
}

interface SectionProgressIndicatorProps {
  sections?: Section[];
}

const SectionProgressIndicator = ({
  sections = [
    { id: 'hero', label: 'Početna' },
    { id: 'value', label: 'Vrednost' },
    { id: 'pricing', label: 'Cene' },
    { id: 'channels', label: 'Kanali' },
    { id: 'benefits', label: 'Prednosti' },
    { id: 'testimonials', label: 'Utisci' },
    { id: 'faq', label: 'Pitanja' },
    { id: 'contact', label: 'Kontakt' },
  ],
}: SectionProgressIndicatorProps) => {
  const [activeSection, setActiveSection] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      setIsVisible(window.scrollY > 300);

      const sectionElements = sections.map((section) =>
        document.getElementById(section.id)
      );

      const currentSectionIndex = sectionElements.findIndex((element) => {
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + window.pageYOffset;
          const elementBottom = elementTop + rect.height;
          return scrollPosition >= elementTop && scrollPosition <= elementBottom;
        }
        return false;
      });

      if (currentSectionIndex !== -1) {
        setActiveSection(currentSectionIndex);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'section_navigation', {
          event_category: 'engagement',
          event_label: `progress_indicator_${sectionId}`,
        });
      }
    }
  };

  const progressPercentage = ((activeSection + 1) / sections.length) * 100;

  if (!isVisible) return null;

  return (
    <>
      <div data-component-id="src/components/common/SectionProgressIndicator.tsx:85:6" data-component-path="src/components/common/SectionProgressIndicator.tsx" data-component-line="85" data-component-end-line="113" data-component-file="SectionProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20lg%3Ablock%20fixed%20left-6%20top-1%2F2%20-translate-y-1%2F2%20z-30%22%7D" className="hidden lg:block fixed left-6 top-1/2 -translate-y-1/2 z-30">
        <div data-component-id="src/components/common/SectionProgressIndicator.tsx:86:8" data-component-path="src/components/common/SectionProgressIndicator.tsx" data-component-line="86" data-component-end-line="112" data-component-file="SectionProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20space-y-4%22%7D" className="flex flex-col space-y-4">
          {sections.map((section, index) => (
            <button data-component-id="src/components/common/SectionProgressIndicator.tsx:88:12" data-component-path="src/components/common/SectionProgressIndicator.tsx" data-component-line="88" data-component-end-line="110" data-component-file="SectionProgressIndicator.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22group%20flex%20items-center%20space-x-3%22%7D"
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              className="group flex items-center space-x-3"
              aria-label={`Idi na ${section.label}`}
            >
              <div data-component-id="src/components/common/SectionProgressIndicator.tsx:94:14" data-component-path="src/components/common/SectionProgressIndicator.tsx" data-component-line="94" data-component-end-line="101" data-component-file="SectionProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={`w-3 h-3 rounded-full transition-all duration-250 ${
                  index === activeSection
                    ? 'bg-primary scale-125 ring-4 ring-primary/30'
                    : index < activeSection
                    ? 'bg-success' :'bg-border hover:bg-muted-foreground'
                }`}
              ></div>
              <span data-component-id="src/components/common/SectionProgressIndicator.tsx:102:14" data-component-path="src/components/common/SectionProgressIndicator.tsx" data-component-line="102" data-component-end-line="109" data-component-file="SectionProgressIndicator.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                className={`text-xs font-medium whitespace-nowrap transition-all duration-250 opacity-0 group-hover:opacity-100 ${
                  index === activeSection
                    ? 'text-primary font-bold' :'text-muted-foreground'
                }`}
              >
                {section.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div data-component-id="src/components/common/SectionProgressIndicator.tsx:115:6" data-component-path="src/components/common/SectionProgressIndicator.tsx" data-component-line="115" data-component-end-line="132" data-component-file="SectionProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Ahidden%20fixed%20bottom-20%20left-0%20right-0%20z-30%20px-4%22%7D" className="lg:hidden fixed bottom-20 left-0 right-0 z-30 px-4">
        <div data-component-id="src/components/common/SectionProgressIndicator.tsx:116:8" data-component-path="src/components/common/SectionProgressIndicator.tsx" data-component-line="116" data-component-end-line="131" data-component-file="SectionProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%2F95%20backdrop-blur-sm%20rounded-full%20shadow-conversion%20p-2%22%7D" className="bg-white/95 backdrop-blur-sm rounded-full shadow-conversion p-2">
          <div data-component-id="src/components/common/SectionProgressIndicator.tsx:117:10" data-component-path="src/components/common/SectionProgressIndicator.tsx" data-component-line="117" data-component-end-line="124" data-component-file="SectionProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20px-4%20py-2%22%7D" className="flex items-center justify-between px-4 py-2">
            <span data-component-id="src/components/common/SectionProgressIndicator.tsx:118:12" data-component-path="src/components/common/SectionProgressIndicator.tsx" data-component-line="118" data-component-end-line="120" data-component-file="SectionProgressIndicator.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-medium%20text-muted-foreground%22%7D" className="text-xs font-medium text-muted-foreground">
              {sections[activeSection]?.label}
            </span>
            <span data-component-id="src/components/common/SectionProgressIndicator.tsx:121:12" data-component-path="src/components/common/SectionProgressIndicator.tsx" data-component-line="121" data-component-end-line="123" data-component-file="SectionProgressIndicator.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20font-bold%20text-primary%22%2C%22textContent%22%3A%22%2F%22%7D" className="text-xs font-bold text-primary">
              {activeSection + 1}/{sections.length}
            </span>
          </div>
          <div data-component-id="src/components/common/SectionProgressIndicator.tsx:125:10" data-component-path="src/components/common/SectionProgressIndicator.tsx" data-component-line="125" data-component-end-line="130" data-component-file="SectionProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-1%20bg-muted%20rounded-full%20overflow-hidden%22%7D" className="h-1 bg-muted rounded-full overflow-hidden">
            <div data-component-id="src/components/common/SectionProgressIndicator.tsx:126:12" data-component-path="src/components/common/SectionProgressIndicator.tsx" data-component-line="126" data-component-end-line="129" data-component-file="SectionProgressIndicator.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-full%20bg-primary%20transition-all%20duration-500%20ease-out%22%7D"
              className="h-full bg-primary transition-all duration-500 ease-out"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SectionProgressIndicator;